// MIPE v2 — effective-dated, explainable pricing engine.
// Consumes seeded reference data (fuel indices, vehicle cost profiles, corridors,
// pricing rule sets) and returns a full explanation payload suitable for writing
// into quote_calculation_runs / quote_line_items.
import { supabase } from "@/integrations/supabase/client";

export type Urgency = "scheduled" | "urgent" | "same_day";

export interface MipeInput {
  origin: string;
  destination: string;
  distanceKm: number;
  weightKg: number;
  vehicleType: string;
  urgency: Urgency;
  region?: "inland" | "coast";
  insurance?: boolean;
  declaredValue?: number;
  addons?: number;
}

export interface MipeLine {
  label: string;
  category:
    | "base" | "distance" | "fuel" | "toll" | "labour" | "maintenance"
    | "urgency" | "insurance" | "handling" | "addon" | "commission" | "vat" | "discount" | "other";
  quantity: number;
  unit_amount: number;
  amount: number;
  source_ref?: string;
}

export interface MipeResult {
  ruleSetId: string | null;
  ruleSetLabel: string;
  sourceSnapshot: Record<string, unknown>;
  inputs: MipeInput;
  lines: MipeLine[];
  subtotal: number;
  commissionRate: number;
  commissionAmount: number;
  vat: number;
  total: number;
  explanation: {
    corridor?: string;
    fuel_source?: string;
    vehicle_profile?: string;
    urgency_multiplier: number;
    notes: string[];
  };
}

const URGENCY_MULT: Record<Urgency, number> = {
  scheduled: 1,
  urgent: 1.25,
  same_day: 1.4,
};

export async function computeMipeQuote(input: MipeInput): Promise<MipeResult> {
  const region = input.region ?? "inland";
  const [{ data: ruleSet }, { data: vcp }, { data: fuel }, { data: corridor }] = await Promise.all([
    supabase.from("pricing_rule_sets").select("*").in("status", ["active", "draft"]).order("status").order("created_at", { ascending: false }).limit(1).maybeSingle(),
    supabase.from("vehicle_cost_profiles").select("*").eq("vehicle_type", input.vehicleType).maybeSingle(),
    supabase.from("fuel_price_indices").select("*").eq("fuel_type", "diesel_50ppm").eq("region", region).order("effective_from", { ascending: false }).limit(1).maybeSingle(),
    supabase.from("corridor_profiles").select("*").or(`origin_hub.ilike.${input.origin},destination_hub.ilike.${input.destination}`).limit(1).maybeSingle(),
  ]);

  const lines: MipeLine[] = [];
  const notes: string[] = [];

  const baseAmount = 1200;
  lines.push({ label: "Base dispatch fee", category: "base", quantity: 1, unit_amount: baseAmount, amount: baseAmount });

  // Distance
  const perKm = 8;
  const distanceAmt = Math.round(input.distanceKm * perKm);
  lines.push({ label: `Distance ${input.distanceKm} km`, category: "distance", quantity: input.distanceKm, unit_amount: perKm, amount: distanceAmt });

  // Fuel — from index + vehicle consumption
  const fuelPrice = Number(fuel?.price_per_litre ?? 22.45);
  const consumption = Number(vcp?.fuel_consumption_l_100km ?? 26);
  const litres = (input.distanceKm * consumption) / 100;
  const fuelAmt = Math.round(litres * fuelPrice);
  lines.push({
    label: `Fuel (${consumption} L/100km × R${fuelPrice.toFixed(2)}/L)`,
    category: "fuel",
    quantity: Math.round(litres * 100) / 100,
    unit_amount: fuelPrice,
    amount: fuelAmt,
    source_ref: fuel ? `fuel_price_indices:${fuel.id}` : undefined,
  });

  // Driver labour
  const driverHourly = Number(vcp?.driver_hourly_zar ?? 110);
  const hours = Number(corridor?.typical_hours ?? Math.max(2, input.distanceKm / 70));
  const driverAmt = Math.round(hours * driverHourly);
  lines.push({
    label: `Driver labour (${hours.toFixed(1)} hrs × R${driverHourly.toFixed(2)})`,
    category: "labour",
    quantity: hours,
    unit_amount: driverHourly,
    amount: driverAmt,
    source_ref: vcp ? `vehicle_cost_profiles:${vcp.id}` : undefined,
  });

  // Maintenance
  const maintPerKm = Number(vcp?.maintenance_per_km_zar ?? 1.65);
  const maintAmt = Math.round(input.distanceKm * maintPerKm);
  lines.push({
    label: `Maintenance (${maintPerKm}/km)`,
    category: "maintenance",
    quantity: input.distanceKm,
    unit_amount: maintPerKm,
    amount: maintAmt,
    source_ref: vcp ? `vehicle_cost_profiles:${vcp.id}` : undefined,
  });

  // Tolls from corridor
  if (corridor?.tolls_estimate_zar) {
    const toll = Number(corridor.tolls_estimate_zar);
    lines.push({
      label: `Tolls (${corridor.code})`,
      category: "toll",
      quantity: 1,
      unit_amount: toll,
      amount: toll,
      source_ref: `corridor_profiles:${corridor.id}`,
    });
    notes.push(`Corridor match ${corridor.code}`);
  } else {
    notes.push("No corridor toll match — tolls not included");
  }

  // Cargo mass surcharge
  const overWeight = Math.max(0, input.weightKg - 1000);
  if (overWeight > 0) {
    const cargoAmt = Math.round(overWeight * 0.9);
    lines.push({ label: `Cargo mass surcharge (>1t)`, category: "handling", quantity: overWeight, unit_amount: 0.9, amount: cargoAmt });
  }

  // Insurance
  if (input.insurance && input.declaredValue) {
    const ins = Math.round(input.declaredValue * 0.015);
    lines.push({ label: "Cargo insurance (1.5%)", category: "insurance", quantity: 1, unit_amount: ins, amount: ins });
  }

  // Addons
  if (input.addons && input.addons > 0) {
    lines.push({ label: "Add-ons", category: "addon", quantity: 1, unit_amount: input.addons, amount: input.addons });
  }

  const preUrgency = lines.reduce((s, l) => s + l.amount, 0);
  const urgencyMult = URGENCY_MULT[input.urgency];
  const urgencyAmt = Math.round(preUrgency * (urgencyMult - 1));
  if (urgencyAmt > 0) {
    lines.push({ label: `Urgency surcharge (${input.urgency})`, category: "urgency", quantity: 1, unit_amount: urgencyMult, amount: urgencyAmt });
  }

  const subtotal = preUrgency + urgencyAmt;
  const commissionRate = input.urgency === "scheduled"
    ? Number(ruleSet?.commission_rate ?? 0.15)
    : Number(ruleSet?.urgent_commission_rate ?? 0.225);
  const commissionAmount = Math.round(subtotal * commissionRate);
  const vat = Math.round((subtotal + commissionAmount) * 0.15);
  const total = subtotal + commissionAmount + vat;

  return {
    ruleSetId: ruleSet?.id ?? null,
    ruleSetLabel: ruleSet ? `${ruleSet.name} ${ruleSet.version} (${ruleSet.status})` : "no-rule-set",
    sourceSnapshot: { fuel, vcp, corridor, ruleSet },
    inputs: input,
    lines,
    subtotal,
    commissionRate,
    commissionAmount,
    vat,
    total,
    explanation: {
      corridor: corridor?.code,
      fuel_source: fuel ? `${fuel.fuel_type}/${fuel.region}` : undefined,
      vehicle_profile: vcp?.vehicle_type,
      urgency_multiplier: urgencyMult,
      notes,
    },
  };
}

export const zar = (n: number) =>
  new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(n);
